import React, { useCallback, useMemo } from "react";

import { faArrowRotateRight } from "@fortawesome/pro-solid-svg-icons";
import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Divider from "antd/es/divider";
import Row from "antd/es/row";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import IntegrationEnvType from "@mapmycustomers/shared/enum/integrations/IntegrationEnvType";
import IntegrationService from "@mapmycustomers/shared/enum/integrations/IntegrationService";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import TextWithInfo from "@app/component/typography/TextWithInfo";
import configService from "@app/config/ConfigService";
import Environment from "@app/enum/Environment";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import { getIntegrationIcon } from "@app/scene/settings/component/Integrations/utils/iconGetters";
import { deleteIntegration } from "@app/store/integrations/actions";
import Integration from "@app/types/integrations/Integration";
import getFrequencyTypeDisplayName from "@app/util/getFrequencyTypeDisplayName";
import useServiceDescription from "@app/util/integrations/useServiceDescription";

import integrationAnalytics from "../../integrationAnalytics";

import styles from "./IntegrationDetailsHeader.module.scss";

interface Props {
  integrationEnvType?: Integration["envType"];
  integrationId?: Integration["id"];
  integrationPortal?: Integration["metadata"];
  integrationSchedule?: Integration["schedule"];
  integrationType?: Integration["type"];
  onDeleteIntegration: typeof deleteIntegration.request;
}

const IntegrationDetailsHeader: React.FC<Props> = ({
  integrationEnvType = IntegrationEnvType.PRODUCTION,
  integrationId,
  integrationPortal,
  integrationSchedule,
  integrationType,
  onDeleteIntegration,
}) => {
  const intl = useIntl();
  const serviceDescription = useServiceDescription(integrationType);
  const isOnSandbox = configService.getCurrentEnvironment() === Environment.SANDBOX;

  const handleDelete = useCallback(
    () => integrationAnalytics.clicked(["Delete"], { integrationId }),
    [integrationId]
  );
  const handleDeleteConfirm = useCallback(() => {
    if (integrationId) {
      integrationAnalytics.clicked(["Delete", "Confirm"], { integrationId });
      onDeleteIntegration(integrationId);
    }
  }, [onDeleteIntegration, integrationId]);
  const handleEdit = useCallback(
    () => integrationAnalytics.clicked(["Edit"], { integrationId }),
    [integrationId]
  );

  const deleteText = useMemo(
    () =>
      intl.formatMessage({
        id: "integrations.home.delete",
        defaultMessage: "Delete",
        description: "Integration home delete link",
      }),
    [intl]
  );

  return (
    <div>
      <Row justify="space-between">
        <Row align="middle">
          {integrationType && (
            <img
              alt={integrationType}
              className={styles.serviceLogo}
              src={getIntegrationIcon(integrationType)}
            />
          )}
          <span className={styles.integrationName}>
            <FormattedMessage
              id="integrations.home.integrationName"
              defaultMessage="{name} Integration {isSandbox, select, true {(Sandbox)} other {}}"
              description="Integration home integration name"
              values={{
                isSandbox: integrationEnvType === IntegrationEnvType.SANDBOX,
                name: serviceDescription?.name ?? "",
              }}
            />
          </span>
          {integrationSchedule &&
          getFrequencyTypeDisplayName(intl, integrationSchedule!.frequency) !== "" ? (
            <Tag
              icon={
                <FontAwesomeIcon className={styles.icon} icon={faArrowRotateRight} size="xs" spin />
              }
            >
              {getFrequencyTypeDisplayName(intl, integrationSchedule!.frequency)}
            </Tag>
          ) : (
            ""
          )}
          {integrationPortal?.portal && (
            <TextWithInfo
              className={styles.instanceDetails}
              info={intl.formatMessage(
                {
                  id: "integrations.home.instance",
                  defaultMessage:
                    "{isSalesforce, select, true  { <b>URL:</b> {portalName}} other {}}{isZohoOrDynamics, select, true {<b>Name:</b> {portalName} {br}} other {}}  <b>Portal ID:</b> {portalId}",
                  description: "Integration home instance detai",
                },
                {
                  b: (text: string) => <b>{text}</b>,
                  br: <br />,
                  isSalesforce: integrationType === IntegrationService.SALESFORCE,
                  isZohoOrDynamics:
                    integrationType === IntegrationService.ZOHO ||
                    integrationType === IntegrationService.DYNAMICS,
                  portalId: integrationPortal.portal.portalId,
                  portalName: integrationPortal.portal.name,
                }
              )}
              placement="right"
            />
          )}
        </Row>
        <Row justify="end">
          <Col>
            <Link
              to={`${Path.SETTINGS}/${SettingPath.ORGANIZATION_INTEGRATIONS}/edit/${
                integrationId ?? ""
              }`}
            >
              <Button
                className={styles.actionButton}
                disabled={!integrationId}
                icon={<FontAwesomeIcon icon={faPen} />}
                onClick={handleEdit}
                type="text"
              >
                {intl.formatMessage({
                  id: "integrations.home.edit",
                  defaultMessage: "Edit",
                  description: "Integration home edit link",
                })}
              </Button>
            </Link>
          </Col>
          <Col>
            {isOnSandbox ? (
              <Tooltip
                placement="bottom"
                title={intl.formatMessage({
                  id: "integrations.home.delete.unable",
                  defaultMessage: "Unable to delete integration while in Sandbox environment.",
                  description: "Integration page. Unable to delete integration",
                })}
              >
                <Button className={styles.actionButton} danger disabled type="link">
                  {deleteText}
                </Button>
              </Tooltip>
            ) : (
              <Popconfirm
                cancelText={intl.formatMessage({
                  id: "integrations.home.action.areYouSure.no",
                  defaultMessage: "No",
                  description: "No-confirmation text for a Delete integration",
                })}
                description={intl.formatMessage({
                  id: "integrations.home.action.areYouSure",
                  defaultMessage: "Are you sure?",
                  description: "Delete integration confirmation",
                })}
                okText={intl.formatMessage({
                  id: "integrations.home.action.areYouSure.yes",
                  defaultMessage: "Yes",
                  description: "Yes-confirmation text for a Delete integration",
                })}
                onConfirm={handleDeleteConfirm}
                placement="left"
              >
                <Button
                  className={styles.actionButton}
                  danger
                  disabled={!integrationId}
                  onClick={handleDelete}
                  type="link"
                >
                  {deleteText}
                </Button>
              </Popconfirm>
            )}
          </Col>
        </Row>
      </Row>
      <Divider orientation="left" />
    </div>
  );
};

export default IntegrationDetailsHeader;
